import validateContractItemsImport from "./validateContractItemsImport";

export const contractSheetName = (value) => String(value || "").replace(/[\\/?*[\]:]/g, " ").trim().slice(0, 31);
export const normalizeContractSheetName = (value) => contractSheetName(value).toLocaleUpperCase("vi");
export const requiresContractCommunes = (section) => ["PROVINCE_COMMUNE", "COMMUNE_DETAIL"].includes(section?.sectionType);
export const getOccupiedContractScopes = (items = []) => {
  const ids = new Set();
  const visit = (rows) => rows.forEach((item) => {
    if (item.contractItemScopeId != null) ids.add(item.contractItemScopeId);
    visit(item.children || []);
  });
  visit(items);
  return ids;
};

// This list supplies both the visible sheet names and the validation targets.
export const getExpectedContractScopes = ({ section, communes = [], selectedCommuneIds = [], occupiedScopes = new Set() }) => {
  if (!section?.isActive) return [];
  const scopes = section.scopes || [];
  const baseType = section.sectionType === "ESTIMATE" ? "ESTIMATE" : section.sectionType === "PROVINCE_COMMUNE" ? "PROVINCE" : null;
  const base = scopes.filter((scope) => scope.isEditable && scope.scopeType === baseType && !occupiedScopes.has(scope.contractItemScopeId))
    .map((scope) => ({ contractSectionId: section.contractSectionId, scopeType: scope.scopeType, communeId: null, sheetName: contractSheetName(scope.scopeName) }));
  if (!requiresContractCommunes(section)) return base;
  return [...base, ...communes.filter((commune) => selectedCommuneIds.includes(commune.communeId)).flatMap((commune) => {
    const scope = scopes.find((entry) => entry.scopeType === "COMMUNE" && entry.communeId === commune.communeId);
    if (scope && (!scope.isEditable || occupiedScopes.has(scope.contractItemScopeId))) return [];
    return [{ contractSectionId: section.contractSectionId, scopeType: "COMMUNE", communeId: commune.communeId,
      sheetName: contractSheetName(scope?.scopeName || commune.communeName) }];
  })];
};

export default function validateContractScopeSheets({ sheets, configuration, section, expectedScopes, unitOptions }) {
  // Contracts predating sections retain their original single-sheet format.
  if (!configuration.sections.length) {
    if (sheets.length !== 1) return { isValid: false, items: [], errors: [{ message: "Hợp đồng chưa có loại hạng mục chỉ hỗ trợ một sheet." }] };
    return validateContractItemsImport({ rows: sheets[0].rows, unitOptions });
  }
  const errors = [];
  const payload = [];
  const error = (sheetName, message) => errors.push({ sheetName, column: "Sheet", message });
  if (!section?.isActive) error("", "Phải chọn loại hạng mục của hợp đồng.");
  if (requiresContractCommunes(section)) {
    if (!configuration.provinceId) error("", "Bắt buộc chọn tỉnh/thành phố trước khi chọn phường/xã.");
    if (!expectedScopes.some((scope) => scope.scopeType === "COMMUNE")) error("", "Bắt buộc chọn ít nhất một phường/xã.");
  }
  if (!expectedScopes.length) error("", "Không có scope trống để import hạng mục.");
  const targets = new Map();
  expectedScopes.forEach((scope) => {
    const name = normalizeContractSheetName(scope.sheetName);
    if (!name || targets.has(name)) error(scope.sheetName, "Tên sheet của các scope bị trùng hoặc để trống. Vui lòng kiểm tra lại phạm vi đã chọn.");
    targets.set(name, scope);
  });
  const found = new Set();
  sheets.forEach((sheet) => {
    const name = normalizeContractSheetName(sheet.sheetName);
    const scope = targets.get(name);
    if (!scope) { error(sheet.sheetName, "Tên sheet không phù hợp với danh sách scope cần import."); return; }
    if (found.has(name)) { error(sheet.sheetName, "Nhiều sheet đang dùng cùng một scope."); return; }
    found.add(name);
    const result = validateContractItemsImport({ rows: sheet.rows, unitOptions });
    errors.push(...result.errors.map((entry) => ({ ...entry, sheetName: sheet.sheetName })));
    if (!result.items.length && !result.errors.length) error(sheet.sheetName, "Sheet không có hạng mục để import.");
    payload.push({ sheetName: sheet.sheetName, contractSectionId: scope.contractSectionId, scopeType: scope.scopeType,
      communeId: scope.communeId, items: result.items });
  });
  targets.forEach((scope, name) => { if (!found.has(name)) error(scope.sheetName, "File thiếu sheet của scope cần import."); });
  return { isValid: !errors.length && payload.length > 0, errors, items: errors.length ? [] : payload.flatMap((sheet) => sheet.items), sheets: errors.length ? [] : payload };
}
